import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Search, Filter, ChevronDown, ChevronRight, Star, Target,
  TrendingUp, TrendingDown, Minus, BookOpen, ArrowLeft,
  BarChart3, Layers, Zap, Clock, Award, X, Info
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeConfig = {
  bullish: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', icon: TrendingUp },
  bearish: { color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', icon: TrendingDown },
  neutral: { color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', icon: Minus },
};

const difficultyConfig = {
  beginner: { color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  intermediate: { color: 'text-[#C5A059]', bg: 'bg-[#C5A059]/10' },
  advanced: { color: 'text-red-400', bg: 'bg-red-500/10' },
};

function ReliabilityStars({ value }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} size={12} className={i <= value ? 'text-[#C5A059] fill-[#C5A059]' : 'text-white/20'} />
      ))}
    </div>
  );
}

function PatternCard({ pattern, onClick }) {
  const [flipped, setFlipped] = useState(false);
  const tc = typeConfig[pattern.type] || typeConfig.neutral;
  const dc = difficultyConfig[pattern.difficulty] || difficultyConfig.beginner;
  const TypeIcon = tc.icon;

  return (
    <div
      className="group perspective-1000 cursor-pointer"
      style={{ perspective: '1000px' }}
      onClick={() => setFlipped(!flipped)}
      data-testid={`pattern-card-${pattern.slug}`}
    >
      <div
        className="relative w-full transition-transform duration-500"
        style={{
          transformStyle: 'preserve-3d',
          transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
          minHeight: '320px',
        }}
      >
        {/* Front */}
        <div
          className="absolute inset-0 bg-[#111111] border border-white/10 rounded-lg p-5 flex flex-col hover:border-[#C5A059]/40 transition-colors"
          style={{ backfaceVisibility: 'hidden' }}
        >
          <div className="flex items-start justify-between mb-3">
            <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${tc.bg} ${tc.color} border ${tc.border}`}>
              <TypeIcon size={12} />
              {pattern.type}
            </div>
            <div className={`px-2 py-1 rounded-full text-xs font-medium ${dc.bg} ${dc.color}`}>
              {pattern.difficulty}
            </div>
          </div>

          {pattern.image_url ? (
            <div className="h-32 mb-3 flex items-center justify-center overflow-hidden rounded">
              <img src={pattern.image_url} alt={pattern.name} className="h-full w-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" loading="lazy" />
            </div>
          ) : (
            <pre className="text-[#C5A059]/60 text-xs font-mono leading-tight mb-3 select-none h-32 flex items-center justify-center">
              {pattern.visual_ascii || ''}
            </pre>
          )}

          <h3 className="font-manrope font-bold text-white text-lg mb-2">{pattern.name}</h3>
          <p className="text-gray-400 text-sm leading-relaxed flex-1 line-clamp-3">{pattern.description}</p>

          <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5">
            <div className="flex items-center gap-2">
              <ReliabilityStars value={pattern.reliability} />
              <span className="text-xs text-gray-500">Reliability</span>
            </div>
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <Target size={12} />
              {pattern.success_probability}%
            </div>
          </div>

          <div className="text-xs text-gray-500 text-center mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
            Click to flip for trading details
          </div>
        </div>

        {/* Back */}
        <div
          className="absolute inset-0 bg-[#0D0D0D] border border-[#C5A059]/30 rounded-lg p-5 flex flex-col overflow-y-auto"
          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
        >
          <h3 className="font-manrope font-bold text-[#C5A059] text-lg mb-3">{pattern.name}</h3>
          <p className="text-gray-400 text-xs mb-3 leading-relaxed">{pattern.description}</p>

          <div className="space-y-2 text-xs flex-1">
            <div className="flex items-center gap-2 text-gray-300">
              <Layers size={12} className="text-[#C5A059] shrink-0" />
              <span className="text-gray-500">Category:</span> {pattern.category}
            </div>
            <div className="flex items-center gap-2 text-gray-300">
              <BarChart3 size={12} className="text-[#007AFF] shrink-0" />
              <span className="text-gray-500">Class:</span> {pattern.pattern_class}
            </div>
            <div className="flex items-center gap-2 text-gray-300">
              <Target size={12} className="text-emerald-400 shrink-0" />
              <span className="text-gray-500">Success Rate:</span> {pattern.success_probability}%
            </div>
          </div>

          <Button
            onClick={(e) => { e.stopPropagation(); onClick(pattern); }}
            className="w-full mt-3 bg-[#C5A059] text-black hover:bg-[#D4AF37] rounded-sm text-xs font-semibold"
            data-testid={`pattern-detail-btn-${pattern.slug}`}
          >
            View Full Analysis
          </Button>
        </div>
      </div>
    </div>
  );
}

function PatternDetail({ pattern, onClose }) {
  const tc = typeConfig[pattern.type] || typeConfig.neutral;
  const dc = difficultyConfig[pattern.difficulty] || difficultyConfig.beginner;
  const TypeIcon = tc.icon;

  const sections = [
    { label: 'Psychology', value: pattern.psychology, icon: BookOpen },
    { label: 'Confirmation Signals', value: pattern.confirmation_signals, icon: Zap },
    { label: 'Volume Behavior', value: pattern.volume_behavior, icon: BarChart3 },
    { label: 'Ideal Entry', value: pattern.ideal_entry, icon: Target },
    { label: 'Stop Loss', value: pattern.stop_loss, icon: TrendingDown },
    { label: 'Take Profit', value: pattern.take_profit, icon: TrendingUp },
    { label: 'Risk/Reward Example', value: pattern.risk_reward_example, icon: Award },
    { label: 'Common Mistakes', value: pattern.common_mistakes, icon: Info },
    { label: 'Best Timeframes', value: pattern.timeframes, icon: Clock },
  ];

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-8 px-4" data-testid="pattern-detail-modal">
      <div className="w-full max-w-3xl bg-[#0D0D0D] border border-white/10 rounded-lg shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-4">
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors" data-testid="pattern-detail-close">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h2 className="font-manrope font-bold text-2xl text-white">{pattern.name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${tc.bg} ${tc.color} border ${tc.border}`}>
                  <TypeIcon size={11} /> {pattern.type}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-xs ${dc.bg} ${dc.color}`}>{pattern.difficulty}</span>
                <span className="text-xs text-gray-500">{pattern.category}</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white p-1" data-testid="pattern-detail-close-x">
            <X size={20} />
          </button>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-3 divide-x divide-white/10 border-b border-white/10">
          <div className="p-4 text-center">
            <div className="flex justify-center mb-1"><ReliabilityStars value={pattern.reliability} /></div>
            <div className="text-xs text-gray-500">Reliability</div>
          </div>
          <div className="p-4 text-center">
            <div className="text-xl font-bold text-[#C5A059]">{pattern.success_probability}%</div>
            <div className="text-xs text-gray-500">Success Rate</div>
          </div>
          <div className="p-4 text-center">
            <div className="text-xl font-bold text-white capitalize">{pattern.pattern_class}</div>
            <div className="text-xs text-gray-500">Pattern Class</div>
          </div>
        </div>

        {/* Pattern image */}
        {pattern.image_url ? (
          <div className="p-6 border-b border-white/10">
            <div className="rounded-lg overflow-hidden bg-black/30 max-h-80 flex items-center justify-center">
              <img src={pattern.image_url} alt={pattern.name} className="max-h-80 w-full object-contain" />
            </div>
          </div>
        ) : pattern.visual_ascii ? (
          <div className="p-6 border-b border-white/10">
            <pre className="text-[#C5A059] text-sm font-mono leading-relaxed text-center bg-black/30 p-4 rounded-lg">
              {pattern.visual_ascii}
            </pre>
          </div>
        ) : null}

        {/* Full description */}
        <div className="p-6 border-b border-white/10">
          <p className="text-gray-300 leading-relaxed">{pattern.description}</p>
        </div>

        {/* Detail sections */}
        <div className="p-6 space-y-4">
          {sections.map(({ label, value, icon: Icon }) => value ? (
            <div key={label} className="bg-[#111111] border border-white/5 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon size={14} className="text-[#C5A059]" />
                <h4 className="font-manrope font-semibold text-sm text-white">{label}</h4>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">{value}</p>
            </div>
          ) : null)}
        </div>

        {/* Related patterns */}
        {pattern.related_patterns?.length > 0 && (
          <div className="px-6 pb-6">
            <h4 className="font-manrope font-semibold text-sm text-white mb-2">Related Patterns</h4>
            <div className="flex flex-wrap gap-2">
              {pattern.related_patterns.map(slug => (
                <span key={slug} className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-xs text-gray-400">
                  {slug.replace(/-/g, ' ')}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


export default function PatternLibrary() {
  const [patterns, setPatterns] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPattern, setSelectedPattern] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [type, setType] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const fetchMeta = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/patterns/meta`);
      setMeta(data);
    } catch (err) { console.error(err); }
  }, []);

  const fetchPatterns = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (type) params.set('type', type);
      if (difficulty) params.set('difficulty', difficulty);
      if (search) params.set('search', search);
      const { data } = await axios.get(`${API}/patterns?${params.toString()}`);
      setPatterns(data.items);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [category, type, difficulty, search]);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);
  useEffect(() => { fetchPatterns(); }, [fetchPatterns]);

  const handleViewDetail = async (pattern) => {
    try {
      const { data } = await axios.get(`${API}/patterns/${pattern.slug}`);
      setSelectedPattern(data);
    } catch (err) { console.error(err); }
  };

  const clearFilters = () => {
    setCategory('');
    setType('');
    setDifficulty('');
    setSearch('');
  };

  const activeFilterCount = [category, type, difficulty].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-lg flex items-center justify-center">
                <BookOpen size={20} className="text-[#C5A059]" />
              </div>
              <div>
                <h1 className="font-manrope font-bold text-4xl sm:text-5xl text-white" data-testid="pattern-library-title">
                  Chart Pattern Library
                </h1>
              </div>
            </div>
            <p className="text-gray-400 text-lg max-w-2xl">
              Master {meta?.total || 43} professional technical analysis patterns. Click any card to flip and explore trading strategies.
            </p>
          </div>

          {/* Search & Filters */}
          <div className="mb-8 space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search patterns..."
                  className="w-full pl-10 pr-4 py-3 bg-black/50 border border-white/10 rounded-sm text-white placeholder:text-white/30 focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] transition-colors text-sm"
                  data-testid="pattern-search-input"
                />
              </div>
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/5 rounded-sm px-6 gap-2"
                data-testid="pattern-filter-toggle"
              >
                <Filter size={14} />
                Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
                <ChevronDown size={14} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </Button>
            </div>

            {showFilters && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 bg-[#111111] border border-white/10 rounded-lg" data-testid="pattern-filters-panel">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Category</label>
                  <select
                    value={category}
                    onChange={e => setCategory(e.target.value)}
                    className="w-full bg-black/50 border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:border-[#C5A059]"
                    data-testid="pattern-filter-category"
                  >
                    <option value="">All Categories</option>
                    {meta?.categories?.map(c => (
                      <option key={c.name} value={c.name}>{c.name} ({c.count})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Signal Type</label>
                  <select
                    value={type}
                    onChange={e => setType(e.target.value)}
                    className="w-full bg-black/50 border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:border-[#C5A059]"
                    data-testid="pattern-filter-type"
                  >
                    <option value="">All Types</option>
                    <option value="bullish">Bullish</option>
                    <option value="bearish">Bearish</option>
                    <option value="neutral">Neutral</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Difficulty</label>
                  <select
                    value={difficulty}
                    onChange={e => setDifficulty(e.target.value)}
                    className="w-full bg-black/50 border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:border-[#C5A059]"
                    data-testid="pattern-filter-difficulty"
                  >
                    <option value="">All Levels</option>
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
                {activeFilterCount > 0 && (
                  <div className="sm:col-span-3">
                    <button
                      onClick={clearFilters}
                      className="text-xs text-[#C5A059] hover:underline"
                      data-testid="pattern-clear-filters"
                    >
                      Clear all filters
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Category quick pills */}
          <div className="flex flex-wrap gap-2 mb-8" data-testid="pattern-category-pills">
            <button
              onClick={() => setCategory('')}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                !category ? 'bg-[#C5A059] text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              All ({meta?.total || 0})
            </button>
            {meta?.categories?.map(c => (
              <button
                key={c.name}
                onClick={() => setCategory(category === c.name ? '' : c.name)}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  category === c.name ? 'bg-[#C5A059] text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
              >
                {c.name} ({c.count})
              </button>
            ))}
          </div>

          {/* Results count */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-gray-500">
              Showing <span className="text-white font-medium">{patterns.length}</span> pattern{patterns.length !== 1 ? 's' : ''}
            </p>
          </div>

          {/* Pattern Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-[#111111] border border-white/10 rounded-lg h-80 animate-pulse" />
              ))}
            </div>
          ) : patterns.length === 0 ? (
            <div className="text-center py-20">
              <BookOpen size={48} className="text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No patterns match your filters</p>
              <button onClick={clearFilters} className="text-[#C5A059] text-sm mt-2 hover:underline">Clear filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="pattern-grid">
              {patterns.map(p => (
                <PatternCard key={p.slug} pattern={p} onClick={handleViewDetail} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedPattern && (
        <PatternDetail pattern={selectedPattern} onClose={() => setSelectedPattern(null)} />
      )}

      <Footer />
    </div>
  );
}
